<script setup lang="ts">
import { computed, inject, markRaw, nextTick, ref, useTemplateRef, watch } from 'vue'
import { debouncedRef } from '@vueuse/core'
import { TreeItem, TreeRoot } from 'reka-ui'
import {
  LayersIcon,
  ListChecksIcon,
  ListTreeIcon,
  NetworkIcon,
  PanelLeftCloseIcon,
} from '@lucide/vue'

import { AnalyticsField, LinkedDataAccount, ReadonlyMode } from '@types'
import { READONLY_MODE } from '@/const'

import { DSButton, DSEmpty, DSEmptyDescription, DSEmptyMedia, DSSkeleton } from '@ds'

import useAnalyticsStore from '@/store/analytics'
import { useModalsStore } from '@/store/modals'
import { useSettingsStore } from '@/store/settings'

import AssetsGrouping from '@/modules/modals/AssetsGrouping.vue'
import AssetsSetPrice from '@/modules/modals/AssetsSetPrice.vue'
import OrganizeFamilyStructure from '@/modules/wizards/OrganizeFamilyStructure/OrganizeFamilyStructure.vue'

import AssetTreeRow from './AssetTreeRow.vue'
import AssetTreeSearch from './AssetTreeSearch.vue'
import AssetTreeSkeleton from './AssetTreeSkeleton.vue'
import { HASH_DELIMITER, SEARCH_DEBOUNCE_DELAY, SEARCH_MIN_LENGTH } from './utils/const'
import {
  buildAssetTree,
  matchAssetTreeSearch,
  pruneAssetTreeForSearch,
} from './utils/buildTreeItems'
import {
  AssetTreeConnectorInfo,
  AssetTreeData,
  AssetTreeItem,
  AssetTreeNodeDescriptionGetter,
  AssetTreeNodeFormatter,
} from './utils/types'
import { stringToLocalDateString } from '@/helpers/dates/index.js'

type Props = {
  data?: AssetTreeData
  loading?: boolean
  caption?: string
  searchPlaceholder?: string
  /** Hides all tree actions for plain picker (e.g. Advisor's client list). */
  hideActions?: boolean
  /** Highlights the row for this path's last key (e.g. currently selected client).
   *  Separate from `currentPath`, which triggers dashboard filter state. */
  activePath?: string[]
  repoName?: string
  formatter: AssetTreeNodeFormatter
  getDescription?: AssetTreeNodeDescriptionGetter
  getConnectorInfo?: (key: string) => AssetTreeConnectorInfo | undefined
  getLinkedAccountInfo?: (key: string) => Promise<LinkedDataAccount> | undefined
  currentPath?: string[]
}

type Emits = {
  'click:name': [path: string[], isLeaf: boolean]
  'click:connector': [path: string[]]
  'click:path': [path: string[]]
  collapse: []
}

const props = withDefaults(defineProps<Props>(), {
  caption: 'Family structure',
  searchPlaceholder: 'Find in structure',
})
const emit = defineEmits<Emits>()

defineOptions({ name: 'DSAssetTree' })

const ROOT_KEY = AnalyticsField.REPO_ROOT

const analyticsStore = useAnalyticsStore()
const modalsStore = useModalsStore()
const settingsStore = useSettingsStore()

const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const manualExpanded = ref<string[]>([])
const isSelecting = ref(false)
const selected = ref<string[]>([])
const activeMatchIndex = ref(0)

const asOfDate = computed(() => stringToLocalDateString(analyticsStore.getDate ?? ''))

/*
 * Uses plain `defineModel` (not `repositoriesStore.treeSearch`) to avoid global state leakage;
 * each instance gets its own search backing, ideal for one-off pickers.
 */
const query = defineModel<string>('search', { default: '' })
const debouncedQuery = debouncedRef(query, SEARCH_DEBOUNCE_DELAY)
const isSearching = computed(() => debouncedQuery.value.trim().length >= SEARCH_MIN_LENGTH)
const effectiveQuery = computed(() => isSearching.value ? debouncedQuery.value : '')

const ownershipAdjusted = computed({
  get: () => settingsStore.getOwnershipAdjustedValues,
  set: value => settingsStore.setOwnershipAdjustedValues(value),
})

const built = computed(() =>
  props.data
    ? buildAssetTree(props.data, ROOT_KEY, props.repoName ?? ROOT_KEY, props.formatter, props.getDescription)
    : undefined,
)
const searchMatch = computed(() => matchAssetTreeSearch(built.value?.searchIndex ?? [], effectiveQuery.value))
const matchCount = computed(() => searchMatch.value.matchedKeys.length)
const activeMatchKey = computed(() => searchMatch.value.matchedKeys[activeMatchIndex.value])

const root = computed(() => {
  if (!built.value?.root) return undefined
  return isSearching.value
    ? pruneAssetTreeForSearch(built.value.root, searchMatch.value.visibleKeys)
    : built.value.root
})
const items = computed<AssetTreeItem[]>(() => root.value ? [root.value] : [])

const expanded = computed<string[]>({
  get: () => [ROOT_KEY, ...(isSearching.value ? [...searchMatch.value.expandKeys] : manualExpanded.value)],
  set: value => {
    if (isSearching.value) return
    manualExpanded.value = value.filter(key => key !== ROOT_KEY)
  },
})

const activeNodeKey = computed(() => props.currentPath?.at(-1))
const activePathKey = computed(() => props.activePath?.at(-1))

const getKey = (item: AssetTreeItem) => item.key
const getChildren = (item: AssetTreeItem) => item.children

const getAncestorContinues = (path: string[]) =>
  path.slice(1, -1).map((_, index) => !built.value?.isLastChildByKey.get(path.slice(0, index + 2).join(HASH_DELIMITER)))

const getIsLast = (item: AssetTreeItem) => built.value?.isLastChildByKey.get(item.key) ?? true

/*
 * `amount` is the raw transaction-entry amount (see docs/ai-context/asset-vs-account.md) — a
 * holding's share quantity for a position, but a bare ownership *fraction* for an account/holder
 * edge, where a trivial 1 (100%) reads as clutter rather than information. Hiding exactly 1 is a
 * display heuristic, not a type check — it can hide a real one-unit position, but matches what
 * the design only shows a second value for: actual quantities, not pass-through ownership.
 */
const getAmountLabel = (item: AssetTreeItem) => {
  if (item.key === ROOT_KEY || item.amount === undefined || item.amount === 1) return undefined
  return analyticsStore.amountFormatter(item.amount)
}

const toggleSelected = (item: AssetTreeItem) => {
  const id = item.path.at(-1) as string
  selected.value = selected.value.includes(id)
    ? selected.value.filter(value => value !== id)
    : [...selected.value, id]
}

const handleMenuSelect = (item: AssetTreeItem) => {
  isSelecting.value = true
  toggleSelected(item)
}

const handleCancelSelection = () => {
  isSelecting.value = false
  selected.value = []
}

const handleGroupAccounts = () => {
  const modalInstance = modalsStore.init('assets-grouping', markRaw(AssetsGrouping))
  modalInstance?.open(modalsStore.getZIndex(), {
    ids: selected.value,
    onComplete: handleCancelSelection,
  })
}

const handleSetPrice = (item: AssetTreeItem) => {
  const modalInstance = modalsStore.init('assets-set-price', markRaw(AssetsSetPrice))
  modalInstance?.open(modalsStore.getZIndex(), {
    ids: [item.path.at(-1)],
    onComplete: () => {
      const id = item.path.at(-1) as string
      selected.value = selected.value.filter(value => value !== id)
    },
  })
}

const handleOrganizeFamilyStructure = () => {
  const modalInstance = modalsStore.init('organize-family-structure', markRaw(OrganizeFamilyStructure))
  modalInstance?.open(modalsStore.getZIndex())
}

const handlePrevMatch = () => {
  if (!matchCount.value) return
  activeMatchIndex.value = (activeMatchIndex.value - 1 + matchCount.value) % matchCount.value
}

const handleNextMatch = () => {
  if (!matchCount.value) return
  activeMatchIndex.value = (activeMatchIndex.value + 1) % matchCount.value
}

watch(isSelecting, value => {
  if (value) return
  selected.value = []
})

watch(debouncedQuery, () => {
  activeMatchIndex.value = 0
})

const scrollContainerRef = useTemplateRef<HTMLDivElement>('scrollContainerRef')

watch(activeMatchKey, key => {
  if (!key) return
  nextTick(() => {
    scrollContainerRef.value
      ?.querySelector(`[data-asset-tree-row="${key}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  })
})
</script>

<template>
  <div class="flex flex-col h-full min-h-0 font-ds">
    <div class="flex items-center gap-1 px-2 pt-2 pb-1 shrink-0">
      <span class="flex flex-row items-center flex-1 gap-2 px-1 overflow-hidden">
        <span class="hidden sm:inline text-[11px] font-bold uppercase tracking-ds-wide text-foreground/50 truncate">{{ caption }}</span>
        <template v-if="!hideActions">
          <DSSkeleton
            v-if="loading"
            class="h-5 w-16"
          />
          <span
            v-else
            class="flex items-center gap-1 py-1 px-2 bg-accent dark:bg-white/10 rounded-full text-xs whitespace-nowrap"
          >
            <span class="font-medium text-accent-foreground/50">on</span>
            <span class="font-semibold tracking-ds-tight text-primary-pressed dark:text-accent-foreground">{{ asOfDate }}</span>
          </span>
        </template>
      </span>
      <template v-if="root && !hideActions">
        <DSButton
          variant="ghost"
          size="icon-sm"
          title="Organize family structure"
          class="shrink-0"
          @click="handleOrganizeFamilyStructure"
        >
          <NetworkIcon aria-hidden="true" />
        </DSButton>
        <DSButton
          v-if="!isReadonly"
          variant="ghost"
          size="icon-sm"
          title="Select accounts to group"
          class="shrink-0"
          :class="isSelecting ? 'bg-primary/20 text-brand hover:bg-primary/30' : undefined"
          @click="isSelecting = !isSelecting"
        >
          <ListChecksIcon aria-hidden="true" />
        </DSButton>
      </template>
      <DSButton
        variant="ghost"
        size="icon-sm"
        title="Hide asset tree"
        class="shrink-0"
        @click="emit('collapse')"
      >
        <PanelLeftCloseIcon aria-hidden="true" />
      </DSButton>
    </div>

    <AssetTreeSkeleton v-if="loading" />
    <template v-else-if="root">
      <div class="px-2 pb-2 shrink-0">
        <AssetTreeSearch
          v-model="query"
          :match-count
          :active-match-index
          :search-placeholder
          @prev="handlePrevMatch"
          @next="handleNextMatch"
        />
      </div>

      <div
        ref="scrollContainerRef"
        class="flex-1 min-h-0 overflow-y-auto px-2 pb-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20"
      >
        <TreeRoot
          v-slot="{ flattenItems }"
          v-model:expanded="expanded"
          :items
          :get-key
          :get-children
        >
          <DSEmpty
            v-if="isSearching && !matchCount"
            class="gap-2 p-4"
          >
            <DSEmptyMedia class="mb-0">
              <LayersIcon
                class="size-5 text-muted-foreground/60"
                aria-hidden="true"
              />
            </DSEmptyMedia>
            <DSEmptyDescription class="text-xs">
              Nothing in this tree matches “{{ query }}”.
            </DSEmptyDescription>
          </DSEmpty>
          <template v-else>
            <TreeItem
              v-for="flat in flattenItems"
              :key="flat._id"
              v-slot="{ isExpanded }"
              :value="flat.value"
              :level="flat.level"
              as="div"
              class="bg-transparent!"
            >
              <AssetTreeRow
                :item="flat.value"
                :is-root="flat.value.key === ROOT_KEY"
                :has-children="flat.hasChildren"
                :is-expanded
                :is-last="getIsLast(flat.value)"
                :ancestor-continues="getAncestorContinues(flat.value.path)"
                :query="effectiveQuery"
                :selecting="isSelecting"
                :selected="selected.includes(flat.value.path.at(-1) as string)"
                :is-active-match="flat.value.key === activeMatchKey"
                :is-active="flat.value.path.at(-1) === activePathKey"
                :hide-actions="hideActions"
                :readonly="!!isReadonly"
                :is-filtered="flat.value.path.at(-1) === activeNodeKey"
                :ownership-adjusted
                :connector-info="getConnectorInfo?.(flat.value.path.at(-1) as string)"
                :get-linked-account-info
                :balance-label="analyticsStore.valueFormatter(flat.value.balance ?? 0)"
                :amount-label="getAmountLabel(flat.value)"
                @click:name="emit('click:name', flat.value.path, !flat.hasChildren)"
                @menu:select="handleMenuSelect(flat.value)"
                @menu:edit="emit('click:name', flat.value.path, !flat.hasChildren)"
                @menu:set-price="handleSetPrice(flat.value)"
                @menu:filter="emit('click:path', flat.value.path)"
                @menu:ownership-toggle="ownershipAdjusted = !ownershipAdjusted"
                @menu:edit-repository="emit('click:name', [ROOT_KEY], false)"
                @select="toggleSelected(flat.value)"
              />
            </TreeItem>
          </template>
        </TreeRoot>
      </div>
    </template>
    <DSEmpty
      v-else
      class="gap-2 p-4"
    >
      <DSEmptyMedia class="mb-0">
        <ListTreeIcon
          class="size-5 text-muted-foreground/60"
          aria-hidden="true"
        />
      </DSEmptyMedia>
      <DSEmptyDescription class="text-xs">
        No assets yet.
      </DSEmptyDescription>
    </DSEmpty>

    <div
      v-if="isSelecting && selected.length"
      class="shrink-0 flex items-center gap-2 border-t border-border p-2.5"
    >
      <span class="flex-1 text-xs font-medium text-muted-foreground">{{ selected.length }} selected</span>
      <DSButton
        variant="secondary"
        size="sm"
        @click="handleCancelSelection"
      >
        Cancel
      </DSButton>
      <DSButton
        variant="primary"
        size="sm"
        @click="handleGroupAccounts"
      >
        Group accounts
      </DSButton>
    </div>
  </div>
</template>
